import { useCallback, useEffect, useState } from 'react';
import { usePlan } from '../contexts/PlanContext';
import { usePermissions } from './usePermissions';
import { billingManager } from '../business/billing/BillingManager';
import { calendarDaysFromToday } from '../utils/timeUtils';

export type CardlessTrialState =
  /** Nothing to say — card on file, not a billing-capable tenant, or dismissed. */
  | 'none'
  /** No card on file and the account is still usable → nudge to add one. */
  | 'trial'
  /** Cardless account has lapsed → a card is required to reactivate. */
  | 'cancelled';

export interface CardlessTrialNotice {
  state: CardlessTrialState;
  /**
   * True whenever this tenant has no card on file and needs one — unaffected by
   * dismissal. Persistent affordances (e.g. the header button) key off this so the
   * path to adding a card never disappears; only the banner respects `state`.
   */
  needsCard: boolean;
  /**
   * Days until the trial ends, or null when there is no usable trial end date —
   * callers must handle null and fall back to copy that doesn't mention a deadline.
   */
  daysLeft: number | null;
  dismiss: () => void;
}

/**
 * Whether this tenant needs a payment method. Card-present tenants get nothing: a trial
 * with a card auto-converts, and a card-present cancellation can reactivate directly.
 * Only billing-capable, non-partner tenants are considered.
 *
 * Card presence is read from Chargebee (billingManager.getPaymentMethods), NOT the
 * subscription DTO's PaymentMethodId — that field is only populated by the in-app
 * billing flow and is null for every registration-created subscription, so it cannot
 * tell us whether a card is on file.
 *
 * Deliberately does NOT gate on trial status. The DTO's `status`/`isInTrial` pair does
 * not reliably say "in trial" for subscriptions created at signup — the exact accounts
 * this nudge exists for — so requiring it silently hid the nudge for every cardless
 * trial while the lapsed strip still worked. Chargebee's card list is the one signal we
 * can trust, and "no card on file" is the only precondition that matters: a tenant being
 * billed successfully has a card, so paying customers are excluded by construction.
 */
export const useCardlessTrialNotice = (): CardlessTrialNotice => {
  const { summary, isPartnerManaged } = usePlan();
  const { canViewBilling } = usePermissions();

  // In-memory only — dismissal lasts for this page load; a hard refresh brings the nudge back.
  const [dismissed, setDismissed] = useState(false);
  // null = not yet known (loading or lookup failed) → say nothing until we're sure.
  const [hasCard, setHasCard] = useState<boolean | null>(null);

  const sub = summary?.subscription;
  const lapsed = sub?.status === 'Cancelled' || sub?.status === 'Expired';
  const relevant = !!sub && !isPartnerManaged && canViewBilling;

  useEffect(() => {
    if (!relevant) {
      setHasCard(null);
      return;
    }
    let active = true;
    const load = () => {
      billingManager
        .getPaymentMethods()
        .then((methods) => { if (active) setHasCard(methods.length > 0); })
        // A transient lookup failure must not flash a false "add a card" nudge — stay hidden.
        .catch(() => { if (active) setHasCard(null); });
    };
    load();
    // Re-check when a card is added or removed anywhere in the app, so the nudge clears
    // itself without a refresh.
    window.addEventListener('routemize:payment-methods-changed', load);
    return () => {
      active = false;
      window.removeEventListener('routemize:payment-methods-changed', load);
    };
  }, [relevant, sub?.chargebeeSubscriptionId]);

  const dismiss = useCallback(() => setDismissed(true), []);

  const needsCard = relevant && hasCard === false;
  // A lapsed account can't be dismissed away — adding a card is the only way back in.
  const state: CardlessTrialState = !needsCard
    ? 'none'
    : lapsed
      ? 'cancelled'
      : dismissed
        ? 'none'
        : 'trial';

  // A trial end date that has already passed tells the user nothing useful ("ends in -12
  // days"), so it reads as unknown and the copy drops the countdown entirely.
  const untilTrialEnd = needsCard ? calendarDaysFromToday(sub?.trialEndDate) : null;

  return {
    state,
    needsCard,
    daysLeft: untilTrialEnd !== null && untilTrialEnd >= 0 ? untilTrialEnd : null,
    dismiss,
  };
};
